import { useEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'
import WordMark from '../../assets/images/logos/word_mark_logo.webp'

const MIN_MS = 3000
const EXIT_MS = 400

/**
 * Branded Pofsis splash / default logo loader.
 * - ready=false: stays visible (interim waits like SSO / EntryAccess)
 * - ready=true + minDuration: waits remaining time, fades out, then onExitComplete
 */
export const BrandedSplash = ({
    ready = false,
    minDuration = MIN_MS,
    onExitComplete,
    overlay = true,
}) => {
    const mountedAt = useRef(Date.now())
    const onExitRef = useRef(onExitComplete)
    const [phase, setPhase] = useState('enter') // enter | hold | exit
    const exitStarted = useRef(false)

    useEffect(() => {
        onExitRef.current = onExitComplete
    }, [onExitComplete])

    useEffect(() => {
        if (!ready || exitStarted.current) return

        const elapsed = Date.now() - mountedAt.current
        const wait = Math.max(0, minDuration - elapsed)

        const startExit = () => {
            if (exitStarted.current) return
            exitStarted.current = true
            setPhase('exit')
            window.setTimeout(() => {
                if (typeof onExitRef.current === 'function') onExitRef.current()
            }, EXIT_MS)
        }

        if (wait === 0) {
            startExit()
            return undefined
        }

        const id = window.setTimeout(startExit, wait)
        return () => window.clearTimeout(id)
    }, [ready, minDuration])

    useEffect(() => {
        const id = window.setTimeout(() => setPhase((p) => (p === 'enter' ? 'hold' : p)), 50)
        return () => window.clearTimeout(id)
    }, [])

    return (
        <Box
            sx={{
                position: overlay ? 'fixed' : 'relative',
                inset: overlay ? 0 : undefined,
                height: '100vh',
                width: '100%',
                zIndex: overlay ? 9999 : undefined,
                bgcolor: '#EEF8FF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: phase === 'exit' ? 0 : 1,
                transition: phase === 'exit' ? `opacity ${EXIT_MS}ms ease-in` : undefined,
                pointerEvents: phase === 'exit' ? 'none' : 'auto',
            }}
        >
            <Box
                component="img"
                src={WordMark}
                alt="pofsis"
                sx={{
                    width: 240,
                    maxWidth: '80%',
                    height: 'auto',
                    display: 'block',
                    // Rise from ~3/4 height; grow into final 240px (+10% into place)
                    transform: phase === 'enter'
                        ? 'translateY(25vh) scale(0.909)'
                        : 'translateY(0) scale(1)',
                    opacity: phase === 'enter' ? 0 : 1,
                    transition: 'transform 700ms ease-out, opacity 700ms ease-out',
                    animation: phase === 'hold' ? 'pofsisSplashPulse 1.8s ease-in-out infinite' : 'none',
                    '@keyframes pofsisSplashPulse': {
                        '0%, 100%': { filter: 'brightness(1)' },
                        '50%': { filter: 'brightness(1.06)' },
                    },
                }}
            />
        </Box>
    )
}
